import { Component, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Auth, signInWithEmailAndPassword } from '@angular/fire/auth';
import { doc, Firestore, getDoc, setDoc } from '@angular/fire/firestore';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';

import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { RegisterBusinessComponent } from '../register-business/register-business.component';
import { ServiceOfferDialogComponent } from '../service-offer-dialog/service-offer-dialog.component';

@Component({
  selector: 'app-business-login',
  templateUrl: './business-login.component.html',
  styleUrls: ['./business-login.component.scss'],
})
export class BusinessLoginComponent {
  user: any = { email: '', password: '' };
  isSubmitting = false;

  constructor(
    private auth: Auth,
    private afs: Firestore,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<BusinessLoginComponent>,
    public dialog: MatDialog,

    private uxservice: UxserviceService,
  ) {}

  async loginHandler(form: NgForm, e: Event) {
    e.preventDefault();

    if (form.invalid) {
      Object.keys(form.controls).forEach((key) =>
        form.controls[key]?.markAsTouched(),
      );
      return;
    }

    this.isSubmitting = true;
    this.uxservice.showOverlay('Logging in...');

    try {
      const credential = await signInWithEmailAndPassword(
        this.auth,
        this.user.email,
        this.user.password,
      );
      await this.updateUserCollection(credential.user.uid);
    } catch (error: any) {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('Authentication Failed: ' + error.message);
      console.error(error);
      this.isSubmitting = false;
    }
  }

  private async updateUserCollection(uid: string) {
    const userRef = doc(this.afs, 'users-list', uid);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('Business record not found');
      this.isSubmitting = false;
      return;
    }

    const userData = docSnap.data();

    // 🚫 Reject soft-deleted workers
    if (userData?.['isdeleted'] === true) {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('This account has been deactivated. Please contact your company administrator.');
      this.isSubmitting = false;
      await this.auth.signOut();
      return;
    }

    // 🚫 If disabled vendor account logs in, route to /home (access to vendor dashboard restricted)
    if (userData?.['role'] === 'business' && userData?.['accountStatus'] === 'disabled') {
      await setDoc(
        userRef,
        { lastLoginAt: new Date().toISOString() },
        { merge: true },
      );
      this.uxservice.instantCloseOverlay();
      this.dialogRef.close(true);
      this.router.navigate(['/home']);
      this.isSubmitting = false;
      return;
    }

    // 🚫 If worker's parent vendor is disabled, route to /home
    if (userData?.['role'] === 'worker' && userData?.['parentUid']) {
      try {
        const parentSnap = await getDoc(doc(this.afs, 'users-list', userData['parentUid']));
        if (parentSnap.exists() && parentSnap.data()?.['accountStatus'] === 'disabled') {
          await setDoc(
            userRef,
            { lastLoginAt: new Date().toISOString() },
            { merge: true },
          );
          this.uxservice.instantCloseOverlay();
          this.dialogRef.close(true);
          this.router.navigate(['/home']);
          this.isSubmitting = false;
          return;
        }
      } catch (e) {
        console.error('Error checking parent vendor status:', e);
      }
    }

    if (userData?.['role'] !== 'business' && userData?.['role'] !== 'worker') {
      this.uxservice.instantCloseOverlay();
      this.uxservice.showError('Only business accounts can login here');
      this.isSubmitting = false;

      // 🚨 force logout
      await this.auth.signOut();

      return;
    }

    // ✅ At this point, role = 'business' or 'worker'
    await setDoc(
      userRef,
      { lastLoginAt: new Date().toISOString() },
      { merge: true },
    );
    this.uxservice.instantCloseOverlay();

    this.router.navigate(['/business']);
    this.dialogRef.close(true);
    this.isSubmitting = false;
  }

  openCreatemyaccountbox() {
    this.dialog.open(RegisterBusinessComponent, {
      // width: '70%',
      disableClose: false,
      panelClass: 'dialogupload',
    });
  }

  openServiceOfferDialog() {
    this.router.navigate(['/register'], {
      queryParams: {
        returnUrl: `/setup-role?type=vendor&businessType=rental`
      }
    });
  }
}
